/**
 * Prepara el body de guardado para formularios custom autónomos (sin processFormData de edit/add).
 */

const SYSTEM_ROOT_KEYS = new Set([
  "_id",
  "createdAt",
  "updatedAt",
  "createdBy",
  "updatedBy",
  "__v",
  "idModel",
  "parent",
  "editHistory",
  "textPlainVersion",
  "nid",
]);

const GROUP_ROW_SYSTEM_KEYS = new Set([
  "parent",
  "createdAt",
  "updatedAt",
  "createdBy",
  "updatedBy",
  "__v",
  "textPlainVersion",
  "editHistory",
  "nid",
]);

function isEmpty(value) {
  if (value === null || value === undefined) return true;
  if (value === "") return true;
  if (Array.isArray(value) && value.length === 0) return true;
  if (typeof value === "object" && !Array.isArray(value) && Object.keys(value).length === 0) {
    return true;
  }
  return false;
}

function isFieldHidden(field) {
  return !!field?.hide;
}

function normalizeScalarField(data, fieldDef) {
  const key = fieldDef.key;
  const value = data[key];
  if (value === undefined) return;

  if (fieldDef.type === "reference" && value && typeof value === "object") {
    if (!value._id || value.name === "CARGANDO..." || value.name === "Cargando...") {
      data[key] = null;
    } else {
      data[key] = value._id;
    }
    return;
  }

  if (
    fieldDef.type === "list" &&
    value &&
    typeof value === "object" &&
    !Array.isArray(value)
  ) {
    const id = value._id || value.id;
    data[key] = !id || value.name === "CARGANDO..." || value.name === "Cargando..." ? null : id;
    return;
  }

  if (Array.isArray(value) && fieldDef.type === "reference") {
    data[key] = value
      .map((item) => {
        if (item && typeof item === "object" && item._id) return item._id;
        return typeof item === "string" ? item : null;
      })
      .filter((id) => id != null && id !== "");
    return;
  }

  if (fieldDef.type === "file") {
    return;
  }

  if (isEmpty(value) && !(fieldDef.key && fieldDef.key.startsWith("raw_"))) {
    data[key] = null;
  }
}

/** Hidden subfields that must survive save (links / ids). */
const STRUCTURAL_HIDDEN_GROUP_KEYS = new Set(["posicion", "envio", "parent"]);

function cleanGroupRow(item, subFields) {
  if (!item || typeof item !== "object") return item;
  const cleaned = {};
  (subFields || []).forEach((sub) => {
    if (!sub || !sub.key || item[sub.key] === undefined) return;
    if (isFieldHidden(sub) && !STRUCTURAL_HIDDEN_GROUP_KEYS.has(sub.key)) return;
    cleaned[sub.key] = item[sub.key];
  });
  const _id = item._id;
  if (_id) {
    Object.keys(item).forEach((subKey) => {
      if (subKey === "_id" || Object.prototype.hasOwnProperty.call(cleaned, subKey)) {
        return;
      }
      if (item[subKey] !== undefined) cleaned[subKey] = item[subKey];
    });
    cleaned._id = _id;
  }
  const row = { ...cleaned };
  normalizeFieldsOnObject(row, subFields || []);
  GROUP_ROW_SYSTEM_KEYS.forEach((k) => delete row[k]);
  return row;
}

/**
 * FormV2 calculation rules can materialize the placeholder index "N"
 * (products.N.estcost) when the group array is empty. Coerce back to a real array.
 */
function coerceGroupArrayField(value) {
  if (value == null) return [];
  if (Array.isArray(value)) return value;
  if (typeof value !== "object") return [];

  const keys = Object.keys(value).filter((k) => k !== "N" && k !== "n");
  const numericKeys = keys.filter((k) => /^\d+$/.test(k));
  if (numericKeys.length > 0) {
    return numericKeys
      .sort((a, b) => Number(a) - Number(b))
      .map((k) => value[k])
      .filter((row) => row != null && typeof row === "object");
  }
  // Object shaped like a single row (has subfield keys, not only N)
  const looksLikeRow =
    keys.length > 0 &&
    !Object.prototype.hasOwnProperty.call(value, "N") &&
    !Object.prototype.hasOwnProperty.call(value, "n");
  if (looksLikeRow && value._id) {
    return [value];
  }
  return [];
}

function normalizeFieldsOnObject(data, fields) {
  if (!data || typeof data !== "object" || !Array.isArray(fields)) return;

  fields.forEach((fieldDef) => {
    if (!fieldDef?.key) return;
    const key = fieldDef.key;

    if (fieldDef.type === "group" && fieldDef.isArray) {
      const subFields = fieldDef.fields || fieldDef.itemFields || [];
      const rows = coerceGroupArrayField(data[key]);
      data[key] = rows
        .map((item) => cleanGroupRow(item, subFields))
        .filter((item) => item != null);
      return;
    }

    if (fieldDef.type === "group") {
      return;
    }

    normalizeScalarField(data, fieldDef);

    if (fieldDef.hide && !(fieldDef.type === "group" && fieldDef.isArray && Array.isArray(data[key]) && data[key].length > 0)) {
      if (isEmpty(data[key])) delete data[key];
    }
  });
}

function isDerivedGroupDisplayKey(fieldKey, modelFields) {
  return (modelFields || []).some(
    (f) =>
      f.type === "group" &&
      f.isArray &&
      f.groupDisplayField &&
      fieldKey.startsWith(`${f.key}_`) &&
      fieldKey !== f.key
  );
}

/**
 * Campos `group`: los edita el formulario custom en la raíz, no FormV2 en el bucket.
 * Un bucket sólo puede traer la foto del GET, así que volcarlo pisaría la edición.
 * @param {object[]|null|undefined} modelFields
 * @returns {Set<string>}
 */
function groupFieldKeys(modelFields) {
  const keys = new Set();
  (modelFields || []).forEach((f) => {
    if (f && f.key && f.type === "group") keys.add(String(f.key));
  });
  return keys;
}

/**
 * FormV2 con visualGroups guarda en group_<name>.* — aplanar como processFormData de add/edit.
 */
function flattenVisualGroupBuckets(dataToSave, modelFields) {
  if (!dataToSave || typeof dataToSave !== "object") return;
  const skipKeys = groupFieldKeys(modelFields);
  const groupNames = new Set();
  (modelFields || []).forEach((f) => {
    if (f && f.visualGroup) groupNames.add(String(f.visualGroup));
  });
  groupNames.forEach((name) => {
    const groupKey = `group_${name}`;
    const bucket = dataToSave[groupKey];
    if (!bucket || typeof bucket !== "object" || Array.isArray(bucket)) return;
    Object.keys(bucket).forEach((fieldKey) => {
      if (skipKeys.has(fieldKey)) return;
      dataToSave[fieldKey] = bucket[fieldKey];
    });
    delete dataToSave[groupKey];
  });
  Object.keys(dataToSave).forEach((key) => {
    if (key.startsWith("group_") && dataToSave[key] && typeof dataToSave[key] === "object" && !Array.isArray(dataToSave[key])) {
      const looksLikeVisualBucket = Object.keys(dataToSave[key]).some((fk) =>
        (modelFields || []).some((f) => f && f.key === fk)
      );
      if (looksLikeVisualBucket) {
        Object.keys(dataToSave[key]).forEach((fieldKey) => {
          if (skipKeys.has(fieldKey)) return;
          dataToSave[fieldKey] = dataToSave[key][fieldKey];
        });
        delete dataToSave[key];
      }
    }
  });
}

/**
 * @param {object} form
 * @param {object[]} modelFields
 * @returns {object}
 */
export function prepareCustomFormSavePayload(form, modelFields) {
  const dataToSave = JSON.parse(JSON.stringify(form || {}));
  delete dataToSave.editHistory;
  delete dataToSave.textPlainVersion;

  flattenVisualGroupBuckets(dataToSave, modelFields || []);

  normalizeFieldsOnObject(dataToSave, modelFields || []);

  (modelFields || []).forEach((fieldDef) => {
    if (fieldDef.type === "group" && fieldDef.isArray && fieldDef.groupDisplayField) {
      const prefix = `${fieldDef.key}_`;
      Object.keys(dataToSave).forEach((k) => {
        if (k.startsWith(prefix)) delete dataToSave[k];
      });
    }
  });

  Object.keys(dataToSave).forEach((key) => {
    if (isDerivedGroupDisplayKey(key, modelFields)) {
      delete dataToSave[key];
    }
  });

  return dataToSave;
}
